import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Loader2, Link2 } from "lucide-react";

const SEVERITIES = ["Low", "Medium", "High", "Critical"];

const base = (isNC) => ({
  type: isNC ? "NC" : "CAPA", title: "", description: "", source: "", linkedAuditId: "",
  linkedAuditType: "", findingRef: "", severity: "Medium", category: "", rootCause: "",
  correctiveAction: "", preventiveAction: "", owner: "", dueDate: "", remarks: "",
});

// Reusable CAPA/NC create dialog. `prefill` (e.g. from a failed audit checkpoint) auto-populates
// the source/finding fields — the user never re-types the finding.
export function CapaCreateDialog({ open, onOpenChange, isNC = false, prefill, onCreated }) {
  const [form, setForm] = useState(base(isNC));
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm({ ...base(isNC), ...(prefill || {}) });
  }, [open, prefill, isNC]);

  const fromAudit = !!form.linkedAuditId;

  const doCreate = async () => {
    setConfirm(false); setBusy(true);
    try {
      await api.post("/capa", { ...form });
      toast.success(`${isNC ? "Non-conformity" : "CAPA"} created`);
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">New {isNC ? "Non-Conformity" : "CAPA"}</DialogTitle>
            <DialogDescription className="text-xs">Records a {isNC ? "non-conformity" : "corrective/preventive action"} in the shared ERP after you confirm.</DialogDescription>
          </DialogHeader>

          {fromAudit && (
            <div data-testid="capa-source-banner" className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
              <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Linked to audit <strong>{form.linkedAuditType}</strong>{form.findingRef ? ` · ${form.findingRef}` : ""} — the finding below was carried over automatically.</span>
            </div>
          )}

          <div className="space-y-3">
            <div className="space-y-1"><Label>Title <span className="text-rose-500">*</span></Label><Input data-testid="capa-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="h-9 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => set("severity", v)}>
                  <SelectTrigger data-testid="capa-severity" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Category / Area</Label><Input data-testid="capa-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="h-9 text-sm" /></div>
            </div>
            <div className="space-y-1"><Label>Finding / description</Label><Textarea data-testid="capa-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="min-h-[60px]" /></div>
            <div className="space-y-1"><Label>Root cause</Label><Textarea data-testid="capa-rootcause" value={form.rootCause} onChange={(e) => set("rootCause", e.target.value)} className="min-h-[50px]" /></div>
            {!isNC && (
              <>
                <div className="space-y-1"><Label>Corrective action</Label><Textarea data-testid="capa-corrective" value={form.correctiveAction} onChange={(e) => set("correctiveAction", e.target.value)} className="min-h-[50px]" /></div>
                <div className="space-y-1"><Label>Preventive action</Label><Textarea data-testid="capa-preventive" value={form.preventiveAction} onChange={(e) => set("preventiveAction", e.target.value)} className="min-h-[50px]" /></div>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Responsible person</Label><Input data-testid="capa-owner" value={form.owner} onChange={(e) => set("owner", e.target.value)} className="h-9 text-sm" /></div>
              <div className="space-y-1"><Label>Target date</Label><Input data-testid="capa-due" type="date" value={form.dueDate} onChange={(e) => set("dueDate", e.target.value)} className="h-9 text-sm" /></div>
            </div>
            <div className="space-y-1"><Label>Completion notes</Label><Textarea data-testid="capa-remarks" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} className="min-h-[40px]" /></div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="capa-cancel">Cancel</Button>
            <Button className="bg-amber-700 hover:bg-amber-800" disabled={!form.title.trim() || busy} data-testid="capa-save" onClick={() => setConfirm(true)}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Create {isNC ? "non-conformity" : "CAPA"}</AlertDialogTitle>
            <AlertDialogDescription>This creates a {isNC ? "non-conformity" : "CAPA"} record <strong>{form.title}</strong>{fromAudit ? " linked to the source audit" : ""} in the shared ERP. Proceed?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="capa-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="capa-confirm-save" className="bg-amber-700 hover:bg-amber-800" onClick={doCreate}>Yes, create</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
